import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'
import { useLocation } from 'react-router-dom'
import {
  resolveScreenInfo,
  shouldShowScreenInfoBanner,
} from '@proffer/shared'
import { PageInfoBar } from '../components/UI/PageInfoBar'

export type PageInfoOverride = {
  /** Αντικατάσταση μηνύματος · null = κρύψιμο banner */
  message?: ReactNode | null
  actions?: ReactNode
  hidden?: boolean
}

type PageInfoContextValue = {
  override: PageInfoOverride
  setOverride: (next: PageInfoOverride) => void
}

const PageInfoContext = createContext<PageInfoContextValue | null>(null)

export function PageInfoProvider({ children }: { children: ReactNode }) {
  const [override, setOverrideState] = useState<PageInfoOverride>({})

  const setOverride = useCallback((next: PageInfoOverride) => {
    setOverrideState(next)
  }, [])

  const value = useMemo(
    () => ({ override, setOverride }),
    [override, setOverride],
  )

  return (
    <PageInfoContext.Provider value={value}>{children}</PageInfoContext.Provider>
  )
}

/** Προσαρμογή info banner της τρέχουσας σελίδας (π.χ. κουμπιά δράσης). */
export function usePageInfo(patch?: PageInfoOverride) {
  const setOverride = useContext(PageInfoContext)?.setOverride
  const hasPatch = patch !== undefined
  const hidden = patch?.hidden
  const message = patch?.message
  const actions = patch?.actions

  useEffect(() => {
    if (!setOverride || !hasPatch) return
    setOverride({ hidden, message, actions })
    return () => setOverride({})
  }, [setOverride, hasPatch, hidden, message, actions])
}

export function useResolvedPageInfo(): {
  message: ReactNode | null
  actions?: ReactNode
  hidden: boolean
} {
  const ctx = useContext(PageInfoContext)
  const location = useLocation()
  const fromRoute = resolveScreenInfo(location.pathname)
  const override = ctx?.override ?? {}

  if (override.hidden) {
    return { message: null, actions: override.actions, hidden: true }
  }

  const message =
    override.message !== undefined ? override.message : fromRoute

  return {
    message,
    actions: override.actions,
    hidden: false,
  }
}

export function AutoPageInfoBar({ className }: { className?: string }) {
  const { message, actions, hidden } = useResolvedPageInfo()
  const location = useLocation()
  const hasActions = actions != null
  const visible =
    !hidden &&
    shouldShowScreenInfoBanner(location.pathname, hasActions) &&
    (Boolean(message) || hasActions)

  if (!visible) return null
  return <PageInfoBar message={message} actions={actions} className={className} />
}
